/**
 * LiveBoard — a manager-friendly, read-only overview of every ACTIVE employer
 * and the roles they currently have live (open + shortlisting). Built for a
 * fast "who has what up right now" scan. Used both as its own admin tab and
 * embedded on the Workspace dashboard.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Radio, MapPin, Briefcase, RefreshCw, ArrowRight, Users } from "lucide-react";
import { BASE, authHeader, RoleStatusPill, SectionHeader } from "./workspace/shared";

const cx = (...c) => c.filter(Boolean).join(" ");

function RoleLine({ role, onOpen }) {
  const meta = [role.location, role.employment_type, role.seniority, role.salary_range].filter(Boolean);
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`live-role-${role.id}`}
      className="w-full text-left flex items-start gap-3 rounded-xl border border-[#EEF0F2] bg-[#FAFAF7] px-3 py-2.5 hover:border-[#0D2F4D]/40 hover:bg-white transition-colors"
    >
      <Briefcase className="h-4 w-4 text-[#6F8FA8] mt-0.5 shrink-0" />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-[#0D2F4D] truncate">{role.title}</span>
          <RoleStatusPill status={role.status} />
        </span>
        {meta.length > 0 && (
          <span className="block text-xs text-[#6F8FA8] mt-0.5">{meta.join(" · ")}</span>
        )}
      </span>
    </button>
  );
}

function EmployerCard({ emp, onOpen }) {
  const subtitle = [emp.industry, emp.area].filter(Boolean).join(" · ");
  return (
    <div
      data-testid={`live-employer-${emp.employer_id}`}
      className="rounded-2xl border border-[#E4E7EA] bg-white p-5"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <button
          type="button"
          onClick={() => onOpen?.(emp.employer_id)}
          data-testid={`live-employer-open-${emp.employer_id}`}
          className="text-left group min-w-0"
        >
          <p className="font-display text-lg font-extrabold text-[#0D2F4D] group-hover:underline truncate">
            {emp.company_name}
          </p>
          <p className="text-xs text-[#6F8FA8] mt-0.5 truncate">
            {subtitle || "Active employer"}
            {emp.assigned_to ? ` · Manager: ${emp.assigned_to.split("@")[0]}` : ""}
          </p>
        </button>
        <span
          className={cx(
            "shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold",
            emp.live_count > 0
              ? "bg-[#C6D21F]/25 text-[#0D2F4D] border border-[#C6D21F]"
              : "bg-[#F2F2EF] text-[#94A3B8] border border-[#E4E7EA]",
          )}
        >
          {emp.live_count > 0 && <Radio className="h-3.5 w-3.5" />}
          {emp.live_count} live
        </span>
      </div>
      {emp.live_count > 0 ? (
        <div className="space-y-2">
          {emp.live_roles.map((r) => (
            <RoleLine key={r.id} role={r} onOpen={() => onOpen?.(emp.employer_id)} />
          ))}
        </div>
      ) : (
        <p className="text-xs text-[#94A3B8] italic px-1">No live roles right now.</p>
      )}
    </div>
  );
}

export function LiveBoard({ pw, onOpenEmployer, onSeeAll, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const H = useMemo(() => authHeader(pw), [pw]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BASE}/live-board`, H);
      setData(r.data);
    } catch (err) {
      console.error("LiveBoard: load failed", err);
    } finally {
      setLoading(false);
    }
  }, [H]);
  useEffect(() => { load(); }, [load]);

  const employers = data?.employers || [];
  // On the dashboard we only show employers that actually have live roles,
  // and cap the list so it stays skimmable.
  const shown = compact ? employers.filter((e) => e.live_count > 0).slice(0, 6) : employers;

  return (
    <div className="space-y-5" data-testid="live-board">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Radio className="h-5 w-5 text-[#0D2F4D]" />
          <SectionHeader title="Live board · what's up right now" />
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="live-board-refresh"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-[#6F8FA8] hover:text-[#0D2F4D]"
        >
          <RefreshCw className={cx("h-3.5 w-3.5", loading && "animate-spin")} /> Refresh
        </button>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-[#E4E7EA] bg-white p-4" data-testid="live-board-stat-employers">
          <p className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Active employers</p>
          <p className="font-display text-3xl font-extrabold text-[#0D2F4D] mt-1">
            {data?.total_active_employers ?? "—"}
          </p>
        </div>
        <div className="rounded-2xl border border-[#C6D21F] bg-[#F7F9E8] p-4" data-testid="live-board-stat-roles">
          <p className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Roles live now</p>
          <p className="font-display text-3xl font-extrabold text-[#0D2F4D] mt-1">
            {data?.total_live_roles ?? "—"}
          </p>
        </div>
      </div>

      {loading && !data ? (
        <p className="text-sm text-[#6F8FA8] px-1">Loading the board…</p>
      ) : shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#D6DCE3] bg-[#FAFAF7] p-8 text-center" data-testid="live-board-empty">
          <Users className="h-6 w-6 text-[#94A3B8] mx-auto mb-2" />
          <p className="text-sm text-[#6F8FA8]">
            {compact ? "No active employers have live roles yet." : "No active employers yet. Promote an employer to Active to see them here."}
          </p>
        </div>
      ) : (
        <div className={compact ? "space-y-3" : "grid md:grid-cols-2 gap-4"}>
          {shown.map((emp) => (
            <EmployerCard key={emp.employer_id} emp={emp} onOpen={onOpenEmployer} />
          ))}
        </div>
      )}

      {compact && onSeeAll && (
        <button
          type="button"
          onClick={onSeeAll}
          data-testid="live-board-see-all"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0D2F4D] hover:underline"
        >
          See the full live board <ArrowRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

export function LiveBoardTab({ pw, onOpenEmployer }) {
  return (
    <div data-testid="live-board-tab">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-extrabold text-[#0D2F4D]">Live Board</h1>
        <p className="text-sm text-[#6F8FA8] mt-1">
          Every active employer and the roles they have open right now. Click any employer to jump into their profile.
        </p>
      </div>
      <LiveBoard pw={pw} onOpenEmployer={onOpenEmployer} />
    </div>
  );
}
